import type * as React from "react";
import { cn } from "@/lib/cn";
import { Icon, type IconName } from "../icon/Icon";

/**
 * Pill-shaped selectable chip for filters and categories. When `active`, it is announced with
 * `aria-pressed` and shows a check (selection is color plus icon, never color alone).
 * Ported from the Wanted Design System bundle (components/data-display/Chip.jsx).
 * Tellvue addition: size "xlarge" (48px) for the customer page touch targets.
 */
export interface ChipProps extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, "size"> {
  active?: boolean;
  disabled?: boolean;
  /** @default "medium" */
  size?: "xlarge" | "large" | "medium" | "small" | "xsmall";
  iconLeft?: IconName;
  /** Show a check icon while active. @default true */
  checkWhenActive?: boolean;
  /** Optional trailing count. */
  count?: number;
  children?: React.ReactNode;
}

const SIZES = {
  xlarge: { h: 48, padX: 18, font: "var(--font-size-headline-2)", icon: 18 },
  large: { h: 40, padX: 16, font: "var(--font-size-body-2)", icon: 18 },
  medium: { h: 36, padX: 14, font: "var(--font-size-label-1)", icon: 16 },
  small: { h: 32, padX: 12, font: "var(--font-size-label-1)", icon: 16 },
  xsmall: { h: 26, padX: 10, font: "var(--font-size-label-2)", icon: 14 },
} as const;

export function Chip({
  children,
  active = false,
  disabled = false,
  size = "medium",
  iconLeft,
  checkWhenActive = true,
  count,
  onClick,
  style,
  className,
  ...rest
}: ChipProps) {
  const s = SIZES[size] ?? SIZES.medium;
  const look: React.CSSProperties = disabled
    ? {
        background: "var(--background-normal-normal)",
        color: "var(--label-disable)",
        boxShadow: "inset 0 0 0 1px var(--line-normal-alternative)",
      }
    : active
      ? {
          background: "color-mix(in srgb, var(--primary-normal) 10%, transparent)",
          color: "var(--primary-normal)",
          boxShadow: "inset 0 0 0 1.5px var(--primary-normal)",
        }
      : {
          background: "var(--background-normal-normal)",
          color: "var(--label-normal)",
          boxShadow: "inset 0 0 0 1px var(--line-normal-neutral)",
        };
  const leading = active && checkWhenActive ? "check" : iconLeft;
  return (
    <button
      type="button"
      disabled={disabled}
      aria-pressed={active}
      onClick={onClick}
      className={cn("wds-pressable", className)}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 4,
        minHeight: s.h,
        padding: `0 ${s.padX}px`,
        borderRadius: "var(--radius-full)",
        border: "none",
        fontFamily: "var(--font-sans)",
        fontSize: s.font,
        fontWeight: active ? 600 : 500,
        cursor: disabled ? "not-allowed" : "pointer",
        transition:
          "background var(--duration-fast) var(--ease-standard), box-shadow var(--duration-fast) var(--ease-standard)",
        whiteSpace: "nowrap",
        WebkitTapHighlightColor: "transparent",
        ...look,
        ...style,
      }}
      {...rest}
    >
      {leading && <Icon name={leading} size={s.icon} style={{ flex: "none" }} />}
      {children}
      {count != null && (
        <span
          style={{
            color: active ? "var(--primary-normal)" : "var(--label-neutral)",
            fontWeight: 600,
          }}
        >
          {count}
        </span>
      )}
    </button>
  );
}

export default Chip;
